import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"

/*
 * Geração de texto por IA via Edge Function `ai-generate`. O provedor (Groq por
 * padrão; qualquer API no formato OpenAI) e a chave ficam em secrets da função;
 * o navegador nunca vê a chave.
 */

export type MensagemIA = { role: "system" | "user" | "assistant"; content: string }

// Lista de reserva (Groq) quando o provedor não responde; a lista real vem de useModelosIA().
// Llama 3.x foi descontinuado na Groq em 16/08/2026.
export const MODELOS_IA: ModeloIA[] = [
  { value: "openai/gpt-oss-120b", label: "GPT-OSS 120B" },
  { value: "openai/gpt-oss-20b", label: "GPT-OSS 20B (mais rápido)" },
]

export type ModeloIA = { value: string; label: string }

const MODELOS_DESCONTINUADOS = new Set(["llama-3.3-70b-versatile", "llama-3.1-8b-instant"])

const MODELO_KEY = "groq_model"

export function modeloIAPadrao(): string {
  try {
    const salvo = localStorage.getItem(MODELO_KEY)
    return salvo && !MODELOS_DESCONTINUADOS.has(salvo) ? salvo : MODELOS_IA[0].value
  } catch {
    return MODELOS_IA[0].value
  }
}

export function salvarModeloIAPadrao(model: string) {
  try {
    localStorage.setItem(MODELO_KEY, model)
    // Chave antiga guardada no navegador por versões anteriores
    localStorage.removeItem("groq_api_key")
  } catch {
    /* armazenamento indisponível */
  }
}

async function mensagemDeErro(error: unknown): Promise<string> {
  const context = (error as { context?: Response })?.context
  if (context && typeof context.json === "function") {
    const body = await context.json().catch(() => null)
    if (body?.error) return body.error
  }
  return error instanceof Error ? error.message : "Erro ao chamar a IA."
}

export async function gerarTextoIA(
  messages: MensagemIA[],
  opts: { model?: string; temperature?: number } = {},
): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-generate", {
    body: { messages, model: opts.model ?? modeloIAPadrao(), temperature: opts.temperature ?? 0.3 },
  })
  if (error) throw new Error(await mensagemDeErro(error))
  if (data?.error) throw new Error(data.error)
  return data?.content ?? ""
}

export type StatusIA =
  | { estado: "ok"; mensagem: string; provedor: string; ms?: number }
  | { estado: "nao_implantada" | "sem_chave" | "erro"; mensagem: string }

/**
 * Diagnóstico da integração. Sem `testar`, só confere se a função existe e tem
 * a chave; com `testar`, faz uma geração mínima de verdade no provedor.
 */
export async function verificarIA(testar = false, modelo?: string): Promise<StatusIA> {
  const inicio = performance.now()
  const { data, error } = await supabase.functions.invoke("ai-generate", {
    body: testar
      ? { messages: [{ role: "user", content: "Responda só: ok" }], model: modelo ?? modeloIAPadrao(), temperature: 0 }
      : { action: "status" },
  })

  if (error) {
    const status = (error as { context?: Response })?.context?.status
    if (status === 404) {
      return { estado: "nao_implantada", mensagem: "A Edge Function ai-generate não está publicada no Supabase." }
    }
    const mensagem = await mensagemDeErro(error)
    if (status === 503) return { estado: "sem_chave", mensagem }
    return { estado: "erro", mensagem }
  }
  if (data?.error) return { estado: "erro", mensagem: data.error }

  const provedor: string = data?.provider ?? "Groq"
  return testar
    ? { estado: "ok", provedor, mensagem: `${data?.model ?? provedor} respondeu “${String(data?.content ?? "").trim().slice(0, 40)}”`, ms: Math.round(performance.now() - inicio) }
    : { estado: "ok", provedor, mensagem: `Função publicada, chave configurada. Provedor: ${provedor}, padrão ${data?.defaultModel ?? "—"}.` }
}

// Roteadores do OpenRouter que escolhem o modelo sozinhos.
const ROTULOS_ESPECIAIS: Record<string, string> = {
  "openrouter/free": "Automático grátis (openrouter/free)",
  "openrouter/auto": "Automático pago (openrouter/auto)",
}

const gratis = (id: string) => id.endsWith(":free") || id === "openrouter/free"

// Nome completo (fornecedor/modelo) para não confundir modelos de mesmo nome.
const rotuloModelo = (id: string) =>
  ROTULOS_ESPECIAIS[id] ?? (gratis(id) ? `${id.replace(/:free$/, "")} · grátis` : id)

// Padrão do servidor primeiro, depois roteadores, grátis e o resto em ordem alfabética.
const ordemModelo = (id: string, padrao?: string) =>
  id === padrao ? 0 : ROTULOS_ESPECIAIS[id] ? 1 : gratis(id) ? 2 : 3

/** Modelos de texto ativos na conta do provedor, lidos pela Edge Function. */
export async function listarModelosIA(): Promise<ModeloIA[]> {
  const { data, error } = await supabase.functions.invoke("ai-generate", { body: { action: "models" } })
  if (error) throw new Error(await mensagemDeErro(error))
  if (data?.error) throw new Error(data.error)
  const padrao: string | undefined = data?.defaultModel
  const ids: string[] = (data?.models ?? [])
    .map((m: { id: string }) => m.id)
    .sort((a: string, b: string) => ordemModelo(a, padrao) - ordemModelo(b, padrao) || a.localeCompare(b))
  return ids.length ? ids.map((id) => ({ value: id, label: rotuloModelo(id) })) : MODELOS_IA
}

export function useModelosIA(atual?: string) {
  const query = useQuery({
    queryKey: ["ai-models"],
    queryFn: listarModelosIA,
    staleTime: 10 * 60_000,
    retry: false,
  })
  const modelos = query.data ?? MODELOS_IA
  // Mantém o selecionado visível mesmo se não vier na lista.
  const opcoes = atual && !modelos.some((m) => m.value === atual) ? [{ value: atual, label: rotuloModelo(atual) }, ...modelos] : modelos
  return { modelos: opcoes, carregando: query.isLoading, erro: query.error }
}
